import { InputModalityDetector } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  Renderer2,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { HoverCardContentDirective } from './hover-card.directive';
import { IHoverCardPosition } from './hover-card.interface';

const OPEN_DELAY_MS = 150;
/** The card overlaps its trigger a little: a gap would end the hover on the way in. */
const TRIGGER_OVERLAP_PX = 2;
const VIEWPORT_MARGIN_PX = 8;
const INTERACTIVE_SELECTOR = 'a, button, [role="button"]';

/**
 * Small card under (or above) its trigger content. It stays inline in the DOM, so its links follow the trigger in the
 * tab order, but is positioned `fixed`, so no scrolling table cuts it off: below the trigger when it fits in the
 * window, otherwise above, following the trigger while anything scrolls. Opens on mouse hover, on keyboard focus
 * inside, and on a touch tap on a non-interactive part of the trigger; closes on leave, blur, Escape, a click or tap
 * outside, and when another card opens.
 * The card is an `<ng-template hoverCardContent>`; everything else is the trigger.
 */
@Component({
  imports: [NgTemplateOutlet],
  selector: 'app-hover-card',
  styleUrl: './hover-card.component.scss',
  templateUrl: './hover-card.component.html',
})
export class HoverCardComponent {
  /** One card at a time: a keyboard-opened card and a hovered one elsewhere would stack. */
  private static openCard: HoverCardComponent | null = null;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly inputModality = inject(InputModalityDetector);

  /** Nothing to show: the trigger stays as it is. */
  public readonly disabled = input<boolean>(false);

  public readonly content = contentChild.required(HoverCardContentDirective);
  private readonly card = viewChild<ElementRef<HTMLElement>>('card');

  public readonly open = signal<boolean>(false);
  /** Null until measured: the card renders hidden for that first frame. */
  public readonly position = signal<IHoverCardPosition | null>(null);

  @HostBinding('class') public readonly hostClass = 'block';

  private lastPointerType = '';
  /** Closing moved focus back to the trigger: that focus must not reopen the card. */
  private focusReturned = false;
  private lastTrigger: HTMLElement | null = null;
  private openTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.openTimeout);

      if (HoverCardComponent.openCard === this) {
        HoverCardComponent.openCard = null;
      }
    });

    // Measured as soon as the card is in the DOM, before it is painted.
    effect(() => {
      if (this.card()) {
        untracked(() => this.updatePosition());
      }
    });

    // Document listeners only while open: a table renders one card per cell.
    effect((onCleanup) => {
      if (!this.open()) {
        return;
      }

      const reposition = (): void => this.updatePosition();
      // Capture: the table's own horizontal scroll moves the trigger too. A trigger scrolled out of the table or under
      // its sticky columns takes its card with it.
      const onScroll = (): void => (this.isTriggerVisible() ? this.updatePosition() : this.close());
      document.addEventListener('scroll', onScroll, true);
      const stopResize = this.renderer.listen('window', 'resize', reposition);

      const stopPointer = this.renderer.listen('document', 'pointerdown', (event: PointerEvent) => {
        if (!this.host.nativeElement.contains(event.target as Node | null)) {
          this.hide();
        }
      });
      const stopKey = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.close();
        }
      });

      onCleanup(() => {
        document.removeEventListener('scroll', onScroll, true);
        stopResize();
        stopPointer();
        stopKey();
      });
    });
  }

  @HostListener('pointerenter', ['$event'])
  public onPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      return;
    }

    clearTimeout(this.openTimeout);
    this.openTimeout = setTimeout(() => this.show(), OPEN_DELAY_MS);
  }

  /** Keyboard focus on a card link keeps the card open when the mouse wanders off. */
  @HostListener('pointerleave', ['$event'])
  public onPointerLeave(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && !this.isInCard(document.activeElement)) {
      this.hide();
    }
  }

  @HostListener('pointerdown', ['$event'])
  public onPointerDown(event: PointerEvent): void {
    this.lastPointerType = event.pointerType;
  }

  /**
   * Only keyboard focus opens the card: a click, a tap (Android focuses after the tap) or a dialog handing focus back
   * after a mouse close are not keyboard navigation.
   */
  @HostListener('focusin', ['$event'])
  public onFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;

    if (!this.isInCard(target)) {
      this.lastTrigger = target;
    }

    if (this.focusReturned) {
      this.focusReturned = false;
    } else if (this.inputModality.mostRecentModality === 'keyboard') {
      this.show();
    }
  }

  @HostListener('focusout', ['$event'])
  public onFocusOut(event: FocusEvent): void {
    if (!this.host.nativeElement.contains(event.relatedTarget as Node | null)) {
      this.hide();
    }
  }

  /** Touch has no hover: a tap on the trigger's empty area toggles the card; blocks and links keep their own action. */
  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent): void {
    const target = event.target as Element | null;

    if (this.lastPointerType !== 'touch' || target?.closest(INTERACTIVE_SELECTOR) || this.isInCard(target)) {
      return;
    }

    if (this.open()) {
      this.hide();
    } else {
      this.show();
    }
  }

  public show(): void {
    clearTimeout(this.openTimeout);

    if (this.disabled()) {
      this.hide();

      return;
    }

    if (HoverCardComponent.openCard !== this) {
      HoverCardComponent.openCard?.close();
      HoverCardComponent.openCard = this;
    }

    this.open.set(true);
  }

  public hide(): void {
    clearTimeout(this.openTimeout);
    this.open.set(false);
    this.position.set(null);

    if (HoverCardComponent.openCard === this) {
      HoverCardComponent.openCard = null;
    }
  }

  /** Focus on a card link would fall to the page once the card is gone: hand it back to the trigger first. */
  private close(): void {
    if (this.isInCard(document.activeElement) && this.lastTrigger?.isConnected) {
      this.focusReturned = true;
      this.lastTrigger.focus({ preventScroll: true });
    }

    this.hide();
  }

  /** False once the trigger is scrolled out of view or under sticky columns: its start is no longer on top. */
  private isTriggerVisible(): boolean {
    const { height, left, top } = this.host.nativeElement.getBoundingClientRect();

    return this.host.nativeElement.contains(document.elementFromPoint(left + 1, top + height / 2));
  }

  private updatePosition(): void {
    const card = this.card()?.nativeElement;

    if (!card) {
      return;
    }

    const trigger = this.host.nativeElement.getBoundingClientRect();
    const width = card.offsetWidth;
    // Full height even while the max height cuts it, so the card grows back once there is room again.
    const height = card.scrollHeight + card.offsetHeight - card.clientHeight;
    const spaceAbove = trigger.top - VIEWPORT_MARGIN_PX;
    const spaceBelow = window.innerHeight - trigger.bottom - VIEWPORT_MARGIN_PX;
    const above = spaceBelow < height && (spaceAbove >= height || spaceAbove > spaceBelow);
    // Room on neither side (a short window): the card scrolls instead of hiding its links outside the window.
    const maxHeight = (above ? spaceAbove : spaceBelow) + TRIGGER_OVERLAP_PX;

    this.position.set({
      above,
      left: Math.max(VIEWPORT_MARGIN_PX, Math.min(trigger.left, window.innerWidth - width - VIEWPORT_MARGIN_PX)),
      maxHeight,
      top: above ? trigger.top - Math.min(height, maxHeight) + TRIGGER_OVERLAP_PX : trigger.bottom - TRIGGER_OVERLAP_PX,
    });
  }

  private isInCard(element: Element | null): boolean {
    return !!element && !!this.card()?.nativeElement.contains(element);
  }
}
